"use client";

import * as Dialog from "@radix-ui/react-dialog";
import Image from "next/image";
import { useCallback, useState } from "react";
import { Star, X, ArrowLeft, ArrowRight } from "lucide-react";
import { resolveMediaUrl } from "@/lib/media";
import { plainText } from "@/lib/format";
import type { EmbeddedTestimonial, Media } from "@/lib/dto";
import { REVIEWS_SHOWN, REVIEW_TEXT_MAX } from "@/lib/trip/reviews";

type Props = {
  reviews: EmbeddedTestimonial[] | undefined;
  /** Aggregate rating shown in the summary card (1–5). */
  rating?: number;
  reviewsCount?: number;
  /** "Write a Review" link on the summary card. */
  tripadvisorUrl?: string | null;
};

function Stars({ value }: { value: number }) {
  const full = Math.round(value);
  return (
    <span
      className="inline-flex items-center gap-0.5"
      role="img"
      aria-label={`${value} out of 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={15}
          aria-hidden
          className={i < full ? "fill-secondary text-secondary" : "text-line"}
        />
      ))}
    </span>
  );
}

const THUMB_LIMIT = 4;

/** Small row of trip photos a reviewer submitted, with a lightbox on click. */
function ReviewGallery({
  photos,
  author,
}: {
  photos: Media[];
  author?: string;
}) {
  const items = photos.filter((p): p is NonNullable<Media> => Boolean(p?.url));
  const [index, setIndex] = useState<number | null>(null);

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    () =>
      setIndex((i) =>
        i == null ? null : (i - 1 + items.length) % items.length,
      ),
    [items.length],
  );
  const next = useCallback(
    () => setIndex((i) => (i == null ? null : (i + 1) % items.length)),
    [items.length],
  );

  if (items.length === 0) return null;
  const visible = items.slice(0, THUMB_LIMIT);
  const overflow = items.length - THUMB_LIMIT;

  return (
    <div className="mt-3 flex gap-2">
      {visible.map((photo, i) => {
        const isLastVisible = i === visible.length - 1;
        const src = resolveMediaUrl(photo.url);
        if (!src) return null;
        return (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={
              isLastVisible && overflow > 0
                ? `View all ${items.length} photos from ${author ?? "this review"}`
                : `View photo ${i + 1} from ${author ?? "this review"}`
            }
            className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-md border border-line"
          >
            <Image
              src={src}
              alt={photo.alt || `${author ?? "Review"} photo ${i + 1}`}
              fill
              sizes="100px"
              className="object-cover transition-transform group-hover:scale-105"
            />
            {isLastVisible && overflow > 0 ? (
              <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center bg-black/50 text-xs font-bold text-white"
              >
                +{overflow}
              </span>
            ) : null}
          </button>
        );
      })}

      <Dialog.Root open={index != null} onOpenChange={(o) => !o && close()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm" />
          <Dialog.Content className="fixed inset-0 z-50 grid place-items-center p-4">
            <Dialog.Title className="sr-only">
              {author ? `Photos from ${author}` : "Review photos"}
            </Dialog.Title>
            {index != null ? (
              <div className="relative w-full max-w-4xl">
                <div className="relative aspect-[4/3] w-full">
                  <Image
                    src={resolveMediaUrl(items[index].url) ?? ""}
                    alt={items[index].alt || ""}
                    fill
                    sizes="(min-width: 960px) 896px, 100vw"
                    className="object-contain"
                  />
                </div>
                {items[index].caption ? (
                  <p className="mt-3 text-center text-sm text-white/80">
                    {items[index].caption}
                  </p>
                ) : null}
              </div>
            ) : null}
            <Dialog.Close
              aria-label="Close photo"
              className="absolute top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <X size={18} aria-hidden />
            </Dialog.Close>
            {items.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Previous photo"
                  className="absolute left-4 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                  <ArrowLeft size={18} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Next photo"
                  className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                  <ArrowRight size={18} aria-hidden />
                </button>
              </>
            ) : null}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

/**
 * Traveller reviews — matches production's real markup exactly (confirmed via
 * the live DOM): a "Traveller Reviews" heading, a TripAdvisor rating summary
 * card (`border-primary bg-primary/15`) with a "Write a Review" CTA, then a
 * plain list of reviews, each with stars, a one-line title, a scrollable body
 * and an avatar-initial footer with the reviewer's name and date.
 *
 * Replaces an invented "What travellers say" heading with a multi-platform
 * FancyReview badge row and boxed review cards.
 */
export function PackageReviews({
  reviews,
  rating,
  reviewsCount,
  tripadvisorUrl,
}: Props) {
  const items = (reviews ?? []).filter((r) => r.content || r.title);
  if (!items.length) return null;

  return (
    <section id="reviews" className="defer-render scroll-mt-32">
      <div className="mb-8">
        <h2 className="font-secondary text-lg font-extrabold text-headings md:text-xl lg:text-[1.675rem]">
          Traveller Reviews
        </h2>
      </div>

      {rating ? (
        <aside className="mb-8 rounded-md border border-primary bg-primary/15 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-x-2 text-[1.125rem] font-bold">
                <svg className="h-[30px] w-[141px]" aria-hidden>
                  <use xlinkHref="/icons.svg#tripadvisorbase" />
                </svg>{" "}
                Rating
              </h3>
              <p className="flex items-center gap-x-2 pt-2 text-xs font-medium text-headings">
                <Stars value={rating} />
                <b className="rounded-md bg-primary px-2 py-0.5 text-xs font-bold text-white">
                  {rating.toFixed(1)}/5.0
                </b>
                {reviewsCount ? (
                  <span>From {reviewsCount.toLocaleString()} reviews</span>
                ) : null}
              </p>
            </div>
            {tripadvisorUrl ? (
              <a
                href={tripadvisorUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded bg-primary px-6 py-2 text-sm font-bold text-white"
              >
                Write a Review
              </a>
            ) : null}
          </div>
        </aside>
      ) : null}

      <ul className="[&>li+li]:mt-4 [&>li+li]:pt-4">
        {items.slice(0, REVIEWS_SHOWN).map((r) => {
          const avatar = r.avatar?.url ? resolveMediaUrl(r.avatar.url) : null;
          const initial = r.author_name?.trim()?.[0] ?? "";
          return (
            <li key={r.id}>
              <div>
                {r.author_name ? (
                  <div className="flex flex-wrap items-center pb-3">
                    <figure className="relative inline-block h-12 w-12 flex-[0_0_48px] overflow-hidden rounded-full">
                      {avatar ? (
                        <Image
                          src={avatar}
                          alt={r.avatar?.alt || r.author_name}
                          fill
                          sizes="48px"
                          className="object-cover"
                        />
                      ) : (
                        <span className="absolute inset-0 inline-flex items-center justify-center bg-[#e3eff4] text-xl font-bold text-primary capitalize">
                          {initial}
                        </span>
                      )}
                    </figure>
                    <figcaption className="pl-2.5 leading-[1]">
                      <span className="block text-base font-medium text-headings">
                        {r.author_name}
                      </span>
                    </figcaption>
                  </div>
                ) : null}
                <div>
                  {typeof r.rating === "number" ? (
                    <span className="block">
                      <Stars value={r.rating} />
                    </span>
                  ) : null}
                  {r.title ? (
                    <h3 className="line-clamp-1 text-[1.125rem] font-bold">
                      {r.title}
                    </h3>
                  ) : null}
                </div>
                {r.content ? (
                  <div className="mt-4 max-h-[100px] overflow-y-auto">
                    <p className="text-md leading-[1.6] text-muted">
                      {plainText(r.content, REVIEW_TEXT_MAX)}
                    </p>
                  </div>
                ) : null}

                {r.gallery?.length ? (
                  <ReviewGallery photos={r.gallery} author={r.author_name} />
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
